import { useFilterWorkspace } from "@/features/search-filter/hooks/UseFilterWorkspace";
import { Card, FlatButton } from "@stenajs-webui/elements";
import {
  StandardTable,
  StandardTableAction,
  TableContext,
} from "@stenajs-webui/grid";
import React, { memo, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { profileSelectors } from "@/redux/profile/selectors";
import { StoreState } from "@/redux/RootReducer";
import { routePairTargetStandardTableActions } from "../../redux";
import { RoutePairCustomerTargetTableHeader } from "./RoutePairCustomerTargetTableHeader";
import { createRoutePairCustomerTargetTableConfig } from "./config/RoutePairCustomerTargetTableConfig";
import {
  AllCustomerTableColumnKeys,
  AllCustomerTargetsTableRow,
} from "./config/RowsAndColumnsConfig";
import { RoutePairNavigationYear } from "./hooks/UseRoutePairNegotiationYearStatus";
import { routeCustomerTargetSelectors } from "./redux/selectors";
import { sortWithEmptyValuesLast } from "./config/RoutePairTargetSorting";
import { ApolloError } from "@apollo/client";

interface Props {
  tableData: AllCustomerTargetsTableRow[];
  routePairNavigationYear: RoutePairNavigationYear | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  onSearch: () => void;
  searchSkipped: boolean;
}

const _RoutePairCustomerTargetTable: React.FC<Props> = ({
  tableData,
  routePairNavigationYear,
  loading,
  error,
  onSearch,
  searchSkipped,
}) => {
  const dispatch = useDispatch();

  const { openFilter } = useFilterWorkspace();

  const { currentCurrency } = useSelector(profileSelectors.getGlobal);
  const countClicked = useSelector(
    routeCustomerTargetSelectors.getDiscardCountClickedState
  );
  const { isWarningVisible } = useSelector(
    routeCustomerTargetSelectors.getRouteCustomerSortState
  );

  const isRowsSelected = useSelector(
    routeCustomerTargetSelectors.hasSelectedRows
  );

  const routeCustomerTargetEditedData = useSelector(
    routeCustomerTargetSelectors.getEditedCustomerTargetTableRows
  );
  const routeCustomerTargetPersistedData = useSelector(
    routeCustomerTargetSelectors.getPersistedCustomerTargetTableRows
  );
  const listOrder = useSelector(routeCustomerTargetSelectors.getListOrder);
  const isDataModified = useMemo(() => {
    return listOrder.some((order) => {
      return (
        JSON.stringify(routeCustomerTargetEditedData[order]) !==
        JSON.stringify(routeCustomerTargetPersistedData[order])
      );
    });
  }, [
    listOrder,
    routeCustomerTargetEditedData,
    routeCustomerTargetPersistedData,
  ]);

  const standardTableSelector = useCallback(
    (state: StoreState) => routeCustomerTargetSelectors.getTableState(state),
    []
  );
  const standardTableState = useSelector(standardTableSelector);

  const tableContext = useMemo<TableContext<AllCustomerTableColumnKeys>>(() => {
    return {
      state: standardTableState,
      actions: routePairTargetStandardTableActions,
      dispatch: (action: StandardTableAction<AllCustomerTableColumnKeys>) => {
        dispatch(action);
      },
    };
  }, [dispatch, standardTableState]);

  const { isSortingDisabled } = useSelector(
    routeCustomerTargetSelectors.getRouteCustomerSortState
  );

  const isRoutePairStatusDone = routePairNavigationYear?.status === "D";

  const config = useMemo(() => {
    return createRoutePairCustomerTargetTableConfig(
      currentCurrency,
      isRoutePairStatusDone,
      isSortingDisabled,

      isRowsSelected,
      isDataModified,
      !!isWarningVisible,
      searchSkipped
    );
  }, [
    currentCurrency,
    isRoutePairStatusDone,
    isSortingDisabled,
    isRowsSelected,
    isDataModified,
    isWarningVisible,
    searchSkipped,
  ]);

  const { sortBy, desc } = standardTableState.sortOrder;
  const sortedTableData = useMemo(
    () =>
      sortWithEmptyValuesLast(
        tableData ?? [],
        sortBy ? config.columns[sortBy]?.itemValueResolver : undefined,
        desc
      ),
    [tableData, sortBy, desc, config]
  );

  return (
    <Card>
      <RoutePairCustomerTargetTableHeader
        onSearch={onSearch}
        searchSkipped={searchSkipped}
        numShowing={tableData.length}
        tableContext={tableContext}
      />

      <StandardTable
        key={countClicked}
        config={config}
        items={sortedTableData}
        loading={loading}
        noItemsHeader={"Search for customer targets"}
        error={error ? new Error(error.message) : undefined}
        noItemsContentRight={
          <FlatButton label={"Open filter"} onClick={() => openFilter()} />
        }
        noItemsLabel={
          "Please use the filters to search for customer targets.\n"
        }
        tableContext={tableContext}
      />
    </Card>
  );
};

export const RoutePairCustomerTargetTable = memo(_RoutePairCustomerTargetTable);
